import { ArrowLeft, Activity } from 'lucide-react';
import { motion } from 'framer-motion';

interface TermsAndConditionsProps {
  onBack: () => void;
}

export default function TermsAndConditions({ onBack }: TermsAndConditionsProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-gray-900">AgeQ AI</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-gray-600 mb-8">Last updated: December 12, 2024</p>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By accessing and using AgeQ AI ("the App"), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use the App.
              </p>
              <p className="text-gray-700 leading-relaxed">
                These terms apply to all users of the App, including but not limited to browsers, customers, merchants, vendors, and content contributors.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                AgeQ AI is a health tracking and optimization platform that uses artificial intelligence to help users monitor their biological age, track nutrition, and optimize their wellness routines.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The App provides features including but not limited to: food scanning and analysis, health metrics tracking, personalized wellness protocols, and biological age estimation.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Medical Disclaimer</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-4">
                <p className="text-gray-900 font-semibold mb-2">Important Notice</p>
                <p className="text-gray-700 leading-relaxed">
                  AgeQ AI is NOT a medical device and does NOT provide medical advice, diagnosis, or treatment. The information provided by the App is for informational and educational purposes only.
                </p>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Always consult with a qualified healthcare provider before making any health-related decisions</li>
                <li>Do not disregard professional medical advice or delay seeking it because of information from the App</li>
                <li>The App's calculations and estimates are approximations and should not be considered definitive</li>
                <li>Individual results may vary significantly</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-4">As a user of the App, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Use the App in compliance with all applicable laws and regulations</li>
                <li>Not use the App for any unlawful or prohibited purpose</li>
                <li>Not attempt to gain unauthorized access to any portion of the App</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content, features, and functionality of the App, including but not limited to text, graphics, logos, icons, images, audio clips, video clips, data compilations, and software, are the exclusive property of AgeQ AI and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our App without prior written consent.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Subscription and Payment Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Certain features of the App may require a paid subscription. By purchasing a subscription, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Pay all fees associated with your selected subscription plan</li>
                <li>Provide accurate and complete billing information</li>
                <li>Automatic renewal terms unless you cancel before the renewal date</li>
                <li>No refunds for partial subscription periods</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Accuracy and Limitations</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                While we strive for accuracy, the App's food scanning, biological age calculations, and other analyses are estimates based on available data and AI algorithms. We do not guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>100% accuracy of nutritional information from food scans</li>
                <li>Precise biological age calculations</li>
                <li>Specific health outcomes from following App recommendations</li>
                <li>That the App will meet all your specific requirements</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by applicable law,AgeQ AI and its affiliates, officers, employees, agents, partners, and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses resulting from:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Your access to or use of or inability to access or use the App</li>
                <li>Any conduct or content of any third party on the App</li>
                <li>Any content obtained from the App</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Modifications to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. Your continued use of the App after any such changes constitutes your acceptance of the new Terms.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may terminate or suspend your account and access to the App immediately, without prior notice or liability, for any reason, including but not limited to breach of these Terms.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Upon termination, your right to use the App will immediately cease. If you wish to terminate your account, you may do so by discontinuing your use of the App and canceling your subscription.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed and construed in accordance with the laws of the jurisdiction in which AgeQ AI operates, without regard to its conflict of law provisions.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about these Terms and Conditions, please contact us through the Contact Us page or email us at care@ageq.qpp.
              </p>
            </section>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <p className="text-center text-gray-500 text-sm">
            &copy; 2025 AgeQ AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
